(() => {
  if (customElements.get('sam-story-carousel')) return;

  class SamStoryCarousel extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.sam-story-cards__grid');
      this.cards = [...this.track.querySelectorAll('.sam-story-card')];
      this.marquee = this.querySelector('.sam-story-cards__marquee');
      this.dots = [...this.querySelectorAll('[data-story-index]')];
      this.previous = this.querySelector('[data-story-prev]');
      this.next = this.querySelector('[data-story-next]');
      this.mobile = matchMedia('(max-width: 768px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      if (!this.cards.length) return;

      this.addEventListener('shopify:block:select', event => {
        const index = this.cards.indexOf(event.target.closest('.sam-story-card'));
        if (index >= 0) this.goTo(index);
      }, { signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        this.restartAutoAdvance();
        if (button.hasAttribute('data-story-prev')) this.goTo(this.index - 1);
        if (button.hasAttribute('data-story-next')) this.goTo(this.index + 1);
        if (button.hasAttribute('data-story-index')) this.goTo(Number(button.dataset.storyIndex));
      }, { signal });
      this.addEventListener('pointerdown', () => {
        this.interacting = true;
        this.restartAutoAdvance();
      }, { signal });
      window.addEventListener('pointerup', () => { this.interacting = false; }, { signal });
      window.addEventListener('pointercancel', () => { this.interacting = false; }, { signal });
      this.addEventListener('pointerenter', (event) => {
        this.hovering = event.pointerType === 'mouse';
        this.marquee?.getAnimations().forEach((animation) => animation.updatePlaybackRate(this.hovering ? 0.25 : 1));
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hovering = false;
        this.marquee?.getAnimations().forEach((animation) => animation.updatePlaybackRate(1));
      }, { signal });
      this.track.addEventListener('keydown', (event) => {
        if (!this.mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal });
      this.mobile.addEventListener('change', () => this.update(), { signal });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.cards.forEach((card) => this.resizeObserver.observe(card));
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.restartAutoAdvance();
      }, { threshold: 0.25 });
      this.visibilityObserver.observe(this);
      this.classList.add('is-enhanced');
      this.update();
    }

    restartAutoAdvance() {
      clearInterval(this.autoAdvance);
      if (window.Shopify?.designMode) return;
      this.autoAdvance = setInterval(() => {
        if (!this.mobile.matches || !this.visible || document.hidden || this.reducedMotion.matches
          || this.interacting || this.hovering || this.contains(document.activeElement)) return;
        this.goTo((this.index + 1) % this.cards.length);
      }, 2500);
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.cards.length - 1));
      const left = this.cards[target].getBoundingClientRect().left
        - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    update() {
      const left = this.track.getBoundingClientRect().left;
      this.index = this.cards.reduce((closest, card, index) =>
        Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[closest].getBoundingClientRect().left - left) ? index : closest, 0);
      this.dots.forEach((dot, index) => {
        if (index === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.previous) this.previous.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === this.cards.length - 1;
      this.track.tabIndex = this.mobile.matches ? 0 : -1;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearInterval(this.autoAdvance);
      cancelAnimationFrame(this.frame);
    }
  }

  customElements.define('sam-story-carousel', SamStoryCarousel);
})();
